import { memo, useCallback } from 'react'
import { Box, IconButton, TableCell, TableRow } from '@mui/material'
import { Delete, Edit } from '@mui/icons-material'
import { ProcurementRequirementItem } from 'types/lots'
import { useTranslation } from 'react-i18next'
import { IPaginationInfo } from 'types/pagination'
import { FileUpload } from 'components/FileUpload'
import { useDispatch } from 'react-redux'
import { procurementRequirementApi } from 'services/lot/procurement-requirements'

type Props = {
  workRate: Partial<ProcurementRequirementItem>
  index: number
  onEditClick: (idx: number) => void
  onDeleteClick: (idx: number) => void
  pagesInfo: IPaginationInfo
  pageSize: number
  isDisabled: boolean
  onAdd: (workRate: any) => void
  refetch: () => void
}

const WorkRatesTableRowComponent = ({
  workRate,
  index,
  onEditClick,
  onDeleteClick,
  pagesInfo,
  pageSize,
  isDisabled,
  onAdd,
  refetch,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { currentPage } = pagesInfo
  const rowNumber = (currentPage - 1) * pageSize + index + 1

  const handleEditClick = useCallback(() => {
    onEditClick(index)
  }, [index, onEditClick])

  const handleDeleteClick = useCallback(() => {
    onDeleteClick(index)
  }, [index, onDeleteClick])

  const translateAndCapitalize: Function = (text: any) => {
    return text.charAt(0).toLocaleUpperCase('az') + text.slice(1).toLowerCase()
  }

  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell scope="row">{rowNumber}</TableCell>
      <TableCell scope="row">{workRate.name}</TableCell>
      <TableCell scope="row">{workRate.description}</TableCell>
      <TableCell scope="row">{String(workRate.quantity)}</TableCell>
      <TableCell scope="row">
        {workRate.measurement && t(workRate.measurement)}
      </TableCell>
      <TableCell
        sx={{
          width: '240px',
        }}
      >
        <FileUpload
          text={t('extra-document')}
          fileId={workRate.attachedFileId}
          handleFile={(file) => {
            onAdd({ ...workRate, attachedFileId: file.id })
          }}
          previewImage
          accept="image/png, image/gif, image/jpeg"
          onDelete={() =>
            setTimeout(() => {
              refetch()
              dispatch(
                procurementRequirementApi.util.invalidateTags([
                  'AllProcurementItems',
                ])
              )
            }, 0)
          }
          isDisabled={isDisabled}
        />
      </TableCell>
      <TableCell padding="checkbox">
        <Box display="flex" alignItems="center">
          <IconButton disabled={isDisabled} onClick={handleEditClick}>
            <Edit color="primary" fontSize="small" />
          </IconButton>
          <IconButton disabled={isDisabled} onClick={handleDeleteClick}>
            <Delete color="error" fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  )
}

export const WorkRatesTableRow = memo(WorkRatesTableRowComponent)
